/**
 * In-memory cache for GetProductsByManagingBranch responses.
 * Key: branch + kitchen + pagination + server-side list filters.
 */

const cache = new Map();

const MIN_KITCHEN_NAME_LEN = 4;

/**
 * @param {string} branch
 * @param {string} [kitchenNameParam] raw query param; empty / short names = branch-wide (same as API)
 * @param {number} page
 * @param {number} limit
 * @param {object} [filters]
 * @param {string} [filters.product_name_search]
 * @param {string} [filters.product_type] internal select value e.g. None / meal_item
 * @param {string} [filters.categoryKey] sorted joined category names
 * @param {string} [filters.status_type] internal status filter e.g. all / approved
 */
export function productsPageCacheKey(branch, kitchenNameParam, page, limit, filters = {}) {
  const b = String(branch ?? '')
    .trim()
    .toLowerCase()
    .slice(0, 50);
  const kn = kitchenNameParam != null ? String(kitchenNameParam).trim().toLowerCase() : '';
  const kitchenSegment = kn.length >= MIN_KITCHEN_NAME_LEN ? kn : '';
  const search = String(filters.product_name_search ?? '').trim();
  const pt = String(filters.product_type ?? 'None');
  const cat = String(filters.categoryKey ?? '');
  const st = String(filters.status_type ?? 'all');
  return `${b}|${kitchenSegment}|${page}|${limit}|${search}|${pt}|${cat}|${st}`;
}

/**
 * @returns {{ data: object[], totalProducts: number, refreshedAt: number, statusCounts?: Record<string, number> | null } | null}
 */
export function getProductsPageCache(branch, kitchenNameParam, page, limit, filters) {
  return cache.get(productsPageCacheKey(branch, kitchenNameParam, page, limit, filters)) ?? null;
}

/**
 * @param {object} entry
 * @param {object[]} entry.data raw API `data` array
 * @param {number} entry.totalProducts
 * @param {number} [entry.refreshedAt]
 * @param {Record<string, number> | null} [entry.statusCounts] from API `status_counts` (optional)
 */
export function setProductsPageCache(branch, kitchenNameParam, page, limit, entry, filters) {
  const key = productsPageCacheKey(branch, kitchenNameParam, page, limit, filters);
  cache.set(key, {
    data: entry.data,
    totalProducts: entry.totalProducts,
    refreshedAt: entry.refreshedAt ?? Date.now(),
    statusCounts: entry.statusCounts ?? null,
  });
}

/** Drop every cached page for a managing branch (e.g. after a mutation). */
export function clearProductsPageCacheForBranch(branch) {
  const prefix = `${String(branch ?? '')
    .trim()
    .toLowerCase()
    .slice(0, 50)}|`;
  for (const k of [...cache.keys()]) {
    if (k.startsWith(prefix)) {
      cache.delete(k);
    }
  }
}
